import { createContext, useMemo, useState } from 'react'
import { Opacity } from '@pff-consumer/core-ui'
import { Pressable } from 'react-native'
import type { PressableOpacityProps } from './pressable-opacity.props'

export const PressableOpacityContext = createContext<{
  hovered: boolean
  pressed: boolean
  focused: boolean
}>({
  hovered: false,
  pressed: false,
  focused: false,
})

export const PressableOpacity = (props: PressableOpacityProps) => {
  const {
    accessibilityLabel,
    children,
    disabled,
    onPressHandler,
    onLongPressHandler = () => {},
    onFocusHandler = () => {},
    onBlurHandler = () => {},
    style = {},
    overrideHoverStyle = null,
    disabledStyle = null,
    testID,
    role,
    hitSlop = 0,
  } = props

  const hoverStyle = overrideHoverStyle || { opacity: Opacity.onHover }

  const [hovered, setHovered] = useState(false)
  const [pressed, setPressed] = useState(false)
  const [focused, setFocused] = useState(false)

  const contextValue = useMemo(() => ({ hovered, pressed, focused }), [hovered, pressed, focused])

  return (
    <PressableOpacityContext.Provider value={contextValue}>
      <Pressable
        role={role}
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
        onPress={onPressHandler}
        onLongPress={onLongPressHandler}
        onHoverIn={() => {
          onFocusHandler()
          setHovered(true)
        }}
        onHoverOut={() => {
          onBlurHandler()
          setHovered(false)
        }}
        onPressIn={() => {
          onFocusHandler()
          setPressed(true)
        }}
        onPressOut={() => {
          onBlurHandler()
          setPressed(false)
        }}
        onFocus={() => {
          onFocusHandler()
          setFocused(true)
        }}
        onBlur={() => {
          onBlurHandler()
          setFocused(false)
        }}
        // @ts-expect-error
        style={({ hovered, pressed }) => [
          style,
          {
            ...(hovered || pressed || focused ? hoverStyle : { opacity: Opacity.opaque }),
          },
          disabled ? disabledStyle : {},
        ]}
        hitSlop={hitSlop}
        testID={testID}
      >
        {children}
      </Pressable>
    </PressableOpacityContext.Provider>
  )
}
